import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  FileDown, 
  Download,
  Trash2,
  Loader2,
  Calendar,
  Eye,
  User
} from 'lucide-react';
import { formatDateDisplay, formatDateTimeDisplay } from '../../utils/dateUtils';

const ExportsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSchoolAdmin = user?.role === 'school_admin';
  const [exportFiles, setExportFiles] = useState([]);
  const [exportSets, setExportSets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [filesRes, setsRes] = await Promise.all([
        api.get('/export-files'),
        api.get('/export-sets')
      ]);
      setExportFiles(filesRes.data);
      setExportSets(setsRes.data);
    } catch (error) {
      toast.error('Failed to fetch exports');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await api.get(`/export-files/${fileId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this export file?')) return;
    
    try {
      await api.delete(`/export-files/${fileId}`);
      toast.success('File deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleDeleteExportSet = async (exportSetId) => {
    if (!window.confirm('Delete this export set? This cannot be undone.')) return;
    
    try {
      await api.delete(`/export-sets/${exportSetId}`);
      toast.success('Export set deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete export set');
    }
  };

  const handleGenerateExport = async (exportSetId) => {
    try {
      const response = await api.post(`/export-sets/${exportSetId}/generate`);
      toast.success('Export file generated');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate export');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="exports-page">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-slate-900">Exports</h1>
          <p className="text-slate-500 mt-1">Generate and download Veracross import files</p>
        </div>

        {/* Export Sets */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit">Export Sets</CardTitle>
            <CardDescription>Generate CSV exports from conference slots</CardDescription>
          </CardHeader>
          <CardContent>
            {exportSets.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No export sets available</p>
                <p className="text-sm text-slate-400 mt-1">
                  Create a conference set and apply a schedule to generate export sets
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    {isSchoolAdmin && <TableHead>Owner</TableHead>}
                    <TableHead>Active Slots</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exportSets.map((es) => (
                    <TableRow key={es.id} data-testid={`export-set-${es.id}`}>
                      <TableCell className="font-medium">{es.friendly_name}</TableCell>
                      {isSchoolAdmin && (
                        <TableCell>
                          {es.owner_name ? (
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                              <User className="h-3 w-3" />
                              {es.owner_name}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>{es.slots?.filter(s => s.is_active).length || 0}</TableCell>
                      <TableCell>{formatDateDisplay(es.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/export-sets/${es.id}`)}
                            data-testid={`manage-export-${es.id}`}
                            title="Manage Slots"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700"
                            onClick={() => handleGenerateExport(es.id)}
                            data-testid={`generate-export-${es.id}`}
                          >
                            Generate CSV
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-600 hover:text-rose-700"
                            onClick={() => handleDeleteExportSet(es.id)}
                            data-testid={`delete-export-set-${es.id}`}
                            title="Delete Export Set"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Export Files */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit">Generated Files</CardTitle>
            <CardDescription>Download previously generated export files</CardDescription>
          </CardHeader>
          <CardContent>
            {exportFiles.length === 0 ? (
              <div className="text-center py-8">
                <FileDown className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No export files generated yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Filename</TableHead>
                    <TableHead>Conference Set</TableHead>
                    <TableHead>Schedule Set</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exportFiles.map((file) => (
                    <TableRow key={file.id} data-testid={`export-file-${file.id}`}>
                      <TableCell className="font-medium font-mono text-sm">{file.filename}</TableCell>
                      <TableCell>{file.conference_set_name}</TableCell>
                      <TableCell>{file.schedule_set_name}</TableCell>
                      <TableCell>{formatDateTimeDisplay(file.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(file.id, file.filename)}
                            data-testid={`download-file-${file.id}`}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-600"
                            onClick={() => handleDeleteFile(file.id)}
                            data-testid={`delete-file-${file.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ExportsPage;
